import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Dropdown, Spinner } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { moveMultipleSelections } from '../../../../../../../crud/pedidos_api/pedidos';
import { getMesasByLocalId } from '../../../../../../../crud/management_api/mesas';
import { toast } from 'react-toastify';
import './MoveSelectionsModal.scss';

interface MoveSelectionsModalProps {
  show: boolean;
  onClose: () => void;
  selectedItems: Set<string>; // Format: "pedido_id:selection_id"
  currentMesaId: number;
  currentMesaNumber: string;
}

type MesaOption = { value: number; label: string; used?: boolean };

const MoveSelectionsModal: React.FC<MoveSelectionsModalProps> = ({
  show,
  onClose,
  selectedItems,
  currentMesaId,
  currentMesaNumber
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mesas, setMesas] = useState<MesaOption[]>([]);
  const [selected, setSelected] = useState<MesaOption | null>(null);
  const [acceptChecked, setAcceptChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMesas = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const localId = user.local_id ?? 0;
      const resp = await getMesasByLocalId(localId);
      const opts: MesaOption[] = resp.data
        .filter((m: any) => m.state === true && m.mesa_id !== currentMesaId) // Exclude current mesa
        .map((m: any) => ({
          value: m.mesa_id,
          label: m.tag && m.tag.toString().trim() ? String(m.tag) : String(m.mesa_number),
          used: !!m.used,
        }));
      setMesas(opts);
    } catch (err) {
      console.error('Error fetching mesas:', err);
      toast.error('No se pudieron cargar las mesas');
    }
  }, [currentMesaId]);

  useEffect(() => {
    if (show) {
      setIsOpen(true);
      fetchMesas();
    }
  }, [show, fetchMesas]);

  const handleClose = () => {
    setIsOpen(false);
    setSelected(null);
    setAcceptChecked(false);
    onClose();
  };

  const handleSelect = (value: string | null) => {
    const mesaId = value ? Number(value) : null;
    const opt = mesas.find((m) => m.value === mesaId) ?? null;
    setSelected(opt);
    setAcceptChecked(false);
  };

  const buildSelectionsPayload = (): Record<number, string[]> => {
    const payload: Record<number, string[]> = {};
    
    selectedItems.forEach(item => {
      const [pedidoIdStr, selectionIdStr] = item.split(':');
      const pedidoId = Number(pedidoIdStr);
      // Keep selection_id as string (it's a UUID)
      
      if (!payload[pedidoId]) {
        payload[pedidoId] = [];
      }
      payload[pedidoId].push(selectionIdStr);
    });
    
    return payload;
  };

  const handleMove = async () => {
    if (!selected) {
      toast.error('Seleccione una mesa válida');
      return;
    }
    if (selected.used && !acceptChecked) {
      toast.error('Debes aceptar la advertencia para mover a una mesa ocupada');
      return;
    }

    setIsLoading(true);
    try {
      const selectionsToMove = buildSelectionsPayload();
      await moveMultipleSelections(selectionsToMove, selected.value);
      
      const itemCount = selectedItems.size;
      toast.success(
        `${itemCount} ${itemCount === 1 ? 'ítem movido' : 'ítems movidos'} de Mesa ${currentMesaNumber} a Mesa ${selected.label}`
      );
      
      handleClose();
      window.location.reload();
    } catch (err: any) {
      console.error('Error moving selections:', err);
      const errorMessage = err?.response?.data?.message || 'Error al mover las selecciones';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const itemCount = selectedItems.size;

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Mover {itemCount} {itemCount === 1 ? 'selección' : 'selecciones'}
          </Modal.Title>
        </div>
        <Button className="modal-close-button" variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <div className="move-info">
          <p>Mover desde <strong>Mesa {currentMesaNumber}</strong> hacia:</p>
        </div>

        <Form.Group className="mp-formgroup vertical">
          <Form.Label>Selecciona la mesa de destino</Form.Label>

          <Dropdown onSelect={handleSelect}>
            <Dropdown.Toggle variant='none' className='mp-button light'>
              {selected ? `Mesa ${selected.label}` : 'Seleccione una mesa...'}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {mesas.map((m) => (
                <Dropdown.Item 
                  key={m.value} 
                  eventKey={m.value.toString()} 
                  className={m.used ? 'occupied-mesa' : ''}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span>{m.label}</span>
                    {m.used && (
                      <span style={{ color: '#ffc107', fontWeight: 700 }} title="Mesa ocupada">⚠️</span>
                    )}
                  </span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Form.Group>

        {/* Warning and acceptance checkbox when selected mesa is occupied */}
        {selected && selected.used && (
          <div style={{ marginTop: 12 }}>
            <div 
              className="change-warning" 
              style={{ 
                background: 'rgba(255,193,7,0.08)', 
                border: '1px solid rgba(255,193,7,0.3)', 
                padding: '10px', 
                borderRadius: 6 
              }}
            >
              <strong style={{ color: '#b45309' }}>Advertencia:</strong> La mesa seleccionada está ocupada. 
              Las selecciones se agregarán a los pedidos existentes.
            </div>
            <Form.Group style={{ marginTop: 8 }}>
              <Form.Check
                type="checkbox"
                id="accept-move-to-occupied"
                label="Entiendo y deseo continuar"
                checked={acceptChecked}
                onChange={(e) => setAcceptChecked(e.target.checked)}
              />
            </Form.Group>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            className="form-button-create"
            onClick={handleMove}
            disabled={!selected || (selected.used && !acceptChecked) || isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: '8px' }}
                />
                Moviendo...
              </>
            ) : (
              'Mover'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveSelectionsModal;
